/**
 * Contract ↔ invoice linkage. Reference-level matches can be attached in one
 * click; everything else is a suggestion the user confirms, because a wrong
 * link silently distorts certified-value and retention figures downstream.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Link2, Link2Off, Search, Loader2, Zap, ReceiptText, ArrowUpRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Panel, fmtINR, fmtDate, apiError } from './portfolioUi';
import { InvoiceDetailDialog } from './InvoiceDetailDialog';

const BASIS_TONE = {
  REFERENCE: '#15803D',
  VENDOR_PROJECT: '#B45309',
  VENDOR: '#B45309',
  PROJECT: '#71717A',
  SEARCH: '#71717A',
  MANUAL: '#1E4E8C',
};

const MatchBadge = ({ basis, score, reason }) => (
  <span
    title={reason}
    className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold tabular-nums"
    style={{ color: BASIS_TONE[basis] || 'var(--text-muted)', borderColor: `${BASIS_TONE[basis] || '#71717A'}55` }}
  >
    {score >= 100 ? 'exact ref' : score > 0 ? `${score}% match` : 'manual'}
  </span>
);

export const InvoiceLinkagePanel = ({ contractId, contractNumber, onChanged }) => {
  const navigate = useNavigate();
  const [links, setLinks] = useState([]);
  const [linkedValue, setLinkedValue] = useState(0);
  const [candidates, setCandidates] = useState([]);
  const [autoCount, setAutoCount] = useState(0);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [focus, setFocus] = useState(null);   // { invoice, linked }

  const load = useCallback(async (term = '') => {
    setLoading(true);
    try {
      const [l, c] = await Promise.all([
        api.get(`/contracts/contracts/${contractId}/invoice-links`),
        api.get(`/contracts/contracts/${contractId}/invoice-candidates`, { params: term ? { q: term } : {} }),
      ]);
      setLinks(l.data?.links || []);
      setLinkedValue(l.data?.linked_value || 0);
      setCandidates(c.data?.candidates || []);
      setAutoCount(c.data?.auto_linkable || 0);
    } catch {
      toast.error('Could not load invoice linkage');
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => { load(q); }, 350);
    return () => clearTimeout(t);
  }, [q, load]);

  const attach = async (ids, mode = 'manual') => {
    setBusy(true);
    try {
      const { data } = await api.post(`/contracts/contracts/${contractId}/invoice-links`,
        { invoice_ids: ids, mode });
      toast.success(data.linked ? `${data.linked} invoice(s) linked` : (data.message || 'Nothing to link'));
      await load(q);
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e, 'Link failed'));
    } finally { setBusy(false); }
  };

  const detach = async (invoiceId) => {
    try {
      await api.delete(`/contracts/contracts/${contractId}/invoice-links/${invoiceId}`);
      await load(q);
      onChanged?.();
    } catch { toast.error('Unlink failed'); }
  };

  return (
    <Panel
      title="Linked invoices"
      hint={`${links.length} linked · ${fmtINR(linkedValue)} gross against ${contractNumber || 'this contract'}`}
      testId="invoice-linkage-panel"
      right={
        <button
          onClick={() => attach([], 'auto')}
          disabled={busy || !autoCount}
          className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs disabled:opacity-40"
          style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
          data-testid="invoice-auto-link-btn"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
          Auto-link {autoCount ? `(${autoCount})` : ''}
        </button>
      }
    >
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--brand-accent)' }} /></div>
      ) : (
        <>
          <div className="divide-y" style={{ borderColor: 'var(--border-subtle)' }}>
            {links.map((l) => (
              <div key={l.invoice_id} className="flex items-center gap-3 py-3" data-testid={`linked-invoice-${l.invoice_id}`}>
                <ReceiptText className="w-4 h-4 shrink-0" style={{ color: '#15803D' }} />
                <button onClick={() => setFocus({ invoice: { ...(l.invoice || {}), invoice_id: l.invoice_id, reason: l.match_reason }, linked: true })}
                        className="min-w-0 flex-1 text-left" data-testid={`open-invoice-detail-${l.invoice_id}`}>
                  <p className="text-sm truncate" style={{ color: 'var(--text-primary)' }}>
                    {l.invoice?.invoice_no || l.invoice_no}
                    <span className="ml-2 text-[11px]" style={{ color: 'var(--text-muted)' }}>
                      {l.invoice?.vendor_name}
                    </span>
                  </p>
                  <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                    {fmtINR(l.invoice?.gross_amount)} · {l.invoice?.current_stage || '—'} · linked {l.link_mode?.toLowerCase()} · {l.match_reason}
                  </p>
                </button>
                {l.invoice && (
                  <button
                    onClick={() => navigate(`/project-controls/cost/invoices/${l.invoice_id}`)}
                    className="rounded-full border px-2 py-1 text-[11px] inline-flex items-center gap-1"
                    style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}
                    data-testid={`open-invoice-${l.invoice_id}`}
                  >
                    Open <ArrowUpRight className="w-3 h-3" />
                  </button>
                )}
                <button onClick={() => detach(l.invoice_id)}
                        className="rounded-full border px-2 py-1 text-[11px] inline-flex items-center gap-1"
                        style={{ borderColor: 'var(--border-color)', color: '#B91C1C' }}
                        data-testid={`unlink-invoice-${l.invoice_id}`}>
                  <Link2Off className="w-3 h-3" /> Unlink
                </button>
              </div>
            ))}
            {!links.length && (
              <p className="text-sm py-5 text-center" style={{ color: 'var(--text-muted)' }} data-testid="no-linked-invoices">
                No invoice linked yet. Auto-link picks up invoices carrying this contract reference.
              </p>
            )}
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between gap-3 mb-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em]" style={{ color: 'var(--text-muted)' }}>
                Suggested from Invoice &amp; Payment Tracking
              </p>
              <div className="relative w-56">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the register…"
                       className="h-8 pl-8 text-xs" data-testid="invoice-candidate-search" />
              </div>
            </div>
            <div className="divide-y max-h-80 overflow-y-auto" style={{ borderColor: 'var(--border-subtle)' }}>
              {candidates.map((iv) => (
                <div key={iv.invoice_id} className="flex items-center gap-3 py-3" data-testid={`candidate-invoice-${iv.invoice_id}`}>
                  <button onClick={() => setFocus({ invoice: iv, linked: false })}
                          className="min-w-0 flex-1 text-left" data-testid={`open-invoice-detail-${iv.invoice_id}`}>
                    <p className="text-sm truncate" style={{ color: 'var(--text-primary)' }}>
                      {iv.invoice_no}
                      <span className="ml-2 text-[11px]" style={{ color: 'var(--text-muted)' }}>{iv.vendor_name}</span>
                    </p>
                    <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                      {fmtINR(iv.gross_amount)} · due {fmtDate(iv.due_date)} · ref {iv.contract_reference || '—'} · {iv.reason}
                    </p>
                  </button>
                  <MatchBadge basis={iv.basis} score={iv.score} reason={iv.reason} />
                  <button onClick={() => attach([iv.invoice_id])} disabled={busy}
                          className="rounded-full border px-2.5 py-1 text-[11px] inline-flex items-center gap-1"
                          style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                          data-testid={`link-invoice-${iv.invoice_id}`}>
                    <Link2 className="w-3 h-3" /> Link
                  </button>
                </div>
              ))}
              {!candidates.length && (
                <p className="text-sm py-5 text-center" style={{ color: 'var(--text-muted)' }} data-testid="no-invoice-candidates">
                  No candidate invoices. Search the register to attach one manually.
                </p>
              )}
            </div>
          </div>
        </>
      )}
      <InvoiceDetailDialog
        open={!!focus}
        onOpenChange={(o) => !o && setFocus(null)}
        invoice={focus?.invoice}
        linked={focus?.linked}
        contractId={contractId}
        onLinkChanged={() => { load(q); onChanged?.(); }}
      />
    </Panel>
  );
};

export default InvoiceLinkagePanel;
